/**
 * Notification Navigation Service
 *
 * Centralized service for handling navigation from push notifications
 * Maps notification types to proper routes and handles deep linking
 */

import { Capacitor } from '@capacitor/core';
import { navigateFromNativePushRoute } from '../capacitor-init';

export interface NotificationData {
  type: string;
  targetId?: string;
  /** Server-computed SPA path; preferred over parsing type/ids on tap */
  tapPath?: string;
  /** userMovies doc id for review_liked — opens movie with review highlighted */
  highlightReview?: string;
  notificationId?: string;
  navigationType?: string;
  navigationTarget?: string;
  [key: string]: any;
}

function coercePushString(v: unknown): string | undefined {
  if (v == null) return undefined;
  const s = String(v).trim();
  return s.length > 0 ? s : undefined;
}

/**
 * FCM / Capacitor deliver all data values as strings; iOS may nest or omit keys.
 * Ensures custom announcements still navigate when navigationTarget is set.
 */
export function normalizeNotificationData(raw: unknown): NotificationData {
  const flat = flattenPushDataObject(raw);
  const type = String(flat.type ?? flat.Type ?? '').trim() || 'unknown';
  let navigationType = String(flat.navigationType ?? flat.navigation_type ?? '').trim();
  let navigationTarget = String(flat.navigationTarget ?? flat.navigation_target ?? '').trim();
  if (!navigationType && navigationTarget.startsWith('/')) {
    navigationType = 'screen';
  }
  const targetFromFlat = coercePushString(flat.targetId);
  const relatedUser = coercePushString(flat.relatedUserId);
  const entityFromFlat = coercePushString(flat.entityId);
  const followTypes = new Set(['user_followed', 'new_follower', 'follow', 'started_following']);
  /** Shares often store the id as entityId on the notification doc; some clients only surface that key on tap. */
  const shareAndListTypes = new Set([
    'share_movie',
    'share_watchlist',
    'share_profile',
    'share_person',
    'list_shared',
  ]);
  let targetId =
    targetFromFlat ||
    (followTypes.has(type) && relatedUser ? relatedUser : undefined) ||
    (shareAndListTypes.has(type) && entityFromFlat ? entityFromFlat : undefined) ||
    undefined;

  // iOS/FCM sometimes drop targetId but keep type-specific aliases from Cloud Functions
  if (!targetId) {
    if (type === 'crew_post_liked') {
      targetId = coercePushString(flat.postId) || coercePushString(flat.entityId) || targetId;
    } else if (type === 'share_movie' || type === 'review_liked' || type === 'movie_watched' || type === 'movie_recommendation' || type === 'comment_added_on_review') {
      targetId =
        coercePushString(flat.relatedMovieId) ||
        coercePushString(flat.movieId) ||
        (type === 'share_movie' ? entityFromFlat : undefined);
    } else if (type === 'share_watchlist' || type === 'list_shared') {
      targetId =
        coercePushString(flat.relatedWatchlistId) ||
        coercePushString(flat.watchlistId) ||
        (type === 'share_watchlist' ? entityFromFlat : undefined);
    } else if (type === 'share_profile') {
      targetId = coercePushString(flat.profileUserId) || entityFromFlat;
    } else if (type === 'share_person') {
      targetId = coercePushString(flat.personId) || entityFromFlat;
    } else if (type === 'watchlist_liked' || type === 'like_watchlist' || type === 'comment_added_on_watchlist') {
      targetId =
        coercePushString(flat.relatedWatchlistId) ||
        coercePushString(flat.watchlistId) ||
        coercePushString(flat.entityId);
    } else if (type === 'bundle_liked' || type === 'comment_added_on_bundle') {
      targetId =
        coercePushString(flat.bundleId) ||
        coercePushString(flat.entityId);
    } else if (type === 'comment_liked') {
      // Resolve per parentType so the navigator picks the right route.
      const pt = coercePushString(flat.parentType);
      if (pt === 'review') {
        targetId = coercePushString(flat.relatedMovieId) || coercePushString(flat.movieId);
      } else if (pt === 'crewPost') {
        targetId =
          coercePushString(flat.relatedCrewPostId) ||
          coercePushString(flat.crewPostId) ||
          coercePushString(flat.postId);
      } else if (pt === 'watchlist') {
        targetId = coercePushString(flat.relatedWatchlistId) || coercePushString(flat.watchlistId);
      } else if (pt === 'bundle') {
        targetId = coercePushString(flat.bundleId);
      }
    }
  }

  const highlightReview =
    coercePushString(flat.highlightReview) ||
    coercePushString(flat.relatedReviewId) ||
    coercePushString(flat.reviewId);

  return {
    ...flat,
    type,
    navigationType,
    navigationTarget,
    targetId,
    highlightReview,
  };
}

function flattenPushDataObject(raw: unknown): Record<string, string> {
  if (!raw || typeof raw !== 'object') return {};
  const o = raw as Record<string, unknown>;
  // Some iOS / APNs bridges deliver `data` as a JSON string instead of a dict
  if (typeof o.data === 'string' && o.data.trim().startsWith('{')) {
    try {
      const parsed = JSON.parse(o.data) as unknown;
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return flattenPushDataObject(parsed);
      }
    } catch {
      // fall through
    }
  }
  if (!o.type && o.data && typeof o.data === 'object' && !Array.isArray(o.data)) {
    return flattenPushDataObject(o.data);
  }
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(o)) {
    if (v == null || k === 'aps') continue;
    if (typeof v === 'string') {
      if (v.length) out[k] = v;
    } else if (typeof v === 'number' || typeof v === 'boolean') {
      out[k] = String(v);
    } else if (typeof v === 'object' && !Array.isArray(v)) {
      for (const [ik, iv] of Object.entries(v as Record<string, unknown>)) {
        if (iv == null || out[ik] !== undefined) continue;
        if (typeof iv === 'string' && iv.length) {
          out[ik] = iv;
        } else if (typeof iv === 'number' || typeof iv === 'boolean') {
          out[ik] = String(iv);
        }
      }
    }
  }
  return out;
}

function coerceTapPathFromNormalized(n: NotificationData): string | undefined {
  const tp = coercePushString(n.tapPath);
  if (tp?.startsWith('/')) return tp;
  return undefined;
}

/**
 * Navigate to the appropriate screen based on notification type and data
 * @param data - Notification data containing type and targetId
 * @param navigate - React Router navigate function
 */
export const navigateFromNotification = (
  data: NotificationData,
  navigate: (path: string) => void
): void => {
  const normalized = normalizeNotificationData(data);
  const directTap = coerceTapPathFromNormalized(normalized);
  if (directTap) {
    navigate(directTap);
    console.log(`Navigated from push tapPath: ${directTap}`);
    return;
  }

  if (!normalized.type || normalized.type === 'unknown') {
    console.warn('Invalid notification data:', data);
    navigate('/notifications');
    return;
  }

  const { type, targetId } = normalized;

  try {
    switch (type) {
      // Collaboration activity notifications - navigate to watchlist
      case 'movie_added':
      case 'movie_removed':
      case 'note_modified':
      case 'collaboration_activity':
      case 'watchlist_liked':
      case 'like_watchlist':
      case 'collaborator_added':
        if (targetId) {
          navigate(`/watchlist/${targetId}`);
        } else {
          console.warn('Missing targetId for watchlist notification');
          navigate('/notifications');
        }
        break;

      case 'comment_added_on_watchlist':
        navigate(targetId ? `/watchlist/${encodeURIComponent(targetId)}?scrollTo=comments` : '/notifications');
        break;

      case 'bundle_liked':
        navigate(targetId ? `/?bundleId=${encodeURIComponent(targetId)}&openComments=true` : '/');
        break;

      case 'comment_added_on_bundle':
        navigate(targetId ? `/?bundleId=${encodeURIComponent(targetId)}&openComments=true` : '/');
        break;

      // Collaboration invitation - navigate to notifications page to accept/decline
      case 'collaboration_invite':
      case 'collaboration_invitation':
        // Users need to accept/decline from the notifications page
        navigate('/notifications');
        break;

      // Follow notification - navigate to follower's profile
      case 'new_follower':
      case 'user_followed':
        if (targetId) {
          navigate(`/profile/${targetId}`);
        } else {
          console.warn('Missing targetId for follow notification');
          navigate('/notifications');
        }
        break;

      // Review/movie notifications - navigate to movie detail (optionally highlight review)
      case 'review_liked': {
        if (targetId) {
          const hr = normalized.highlightReview;
          const q = hr ? `?highlightReview=${encodeURIComponent(hr)}` : '';
          navigate(`/movie/${encodeURIComponent(targetId)}${q}`);
        } else {
          console.warn('Missing targetId for review_liked notification');
          navigate('/notifications');
        }
        break;
      }
      case 'comment_added_on_review': {
        if (targetId) {
          const hr = normalized.highlightReview;
          const q = hr ? `?highlightReview=${encodeURIComponent(hr)}&scrollTo=comments` : '';
          navigate(`/movie/${encodeURIComponent(targetId)}${q}`);
        } else {
          console.warn('Missing targetId for comment_added_on_review notification');
          navigate('/notifications');
        }
        break;
      }
      case 'movie_recommendation':
      case 'movie_watched':
        if (targetId) {
          navigate(`/movie/${encodeURIComponent(targetId)}`);
        } else {
          console.warn('Missing targetId for movie notification');
          navigate('/notifications');
        }
        break;

      case 'crew_post_liked': {
        const postId =
          targetId ||
          coercePushString(normalized.relatedCrewPostId as any) ||
          coercePushString(normalized.postId as any);
        navigate(postId ? `/?postId=${encodeURIComponent(postId)}&openComments=true` : '/');
        break;
      }
      case 'comment_added_on_crew_post': {
        const postId =
          targetId ||
          coercePushString(normalized.relatedCrewPostId as any) ||
          coercePushString(normalized.postId as any);
        navigate(postId ? `/?postId=${encodeURIComponent(postId)}&openComments=true` : '/');
        break;
      }

      case 'comment_liked': {
        const pt = coercePushString(normalized.parentType as any);
        const cid =
          coercePushString(normalized.commentId as any) ||
          coercePushString(normalized.entityId as any);
        const highlight = cid ? `&highlightCommentId=${encodeURIComponent(cid)}` : '';
        if (pt === 'review') {
          const mid =
            targetId ||
            coercePushString(normalized.relatedMovieId as any) ||
            coercePushString(normalized.movieId as any);
          if (!mid) { navigate('/notifications'); break; }
          const hr =
            coercePushString(normalized.relatedReviewId as any) ||
            coercePushString(normalized.reviewId as any) ||
            normalized.highlightReview;
          const qs = `?scrollTo=comments${hr ? `&highlightReview=${encodeURIComponent(hr)}` : ''}${highlight}`;
          navigate(`/movie/${encodeURIComponent(mid)}${qs}`);
        } else if (pt === 'crewPost') {
          const pid =
            targetId ||
            coercePushString(normalized.relatedCrewPostId as any) ||
            coercePushString(normalized.crewPostId as any) ||
            coercePushString(normalized.postId as any);
          navigate(pid ? `/?postId=${encodeURIComponent(pid)}&openComments=true${highlight}` : '/');
        } else if (pt === 'watchlist') {
          const wid =
            targetId ||
            coercePushString(normalized.relatedWatchlistId as any) ||
            coercePushString(normalized.watchlistId as any);
          navigate(wid ? `/watchlist/${encodeURIComponent(wid)}?scrollTo=comments${highlight}` : '/notifications');
        } else if (pt === 'bundle') {
          const bid = targetId || coercePushString(normalized.bundleId as any);
          navigate(bid ? `/?bundleId=${encodeURIComponent(bid)}&openComments=true${highlight}` : '/');
        } else {
          navigate('/notifications');
        }
        break;
      }

      // Access control notifications - navigate to home
      case 'access_approved':
      case 'access_rejected':
        navigate('/');
        break;

      case 'custom_announcement': {
        const navType = normalized.navigationType;
        const navTarget = normalized.navigationTarget;
        const screenPath =
          navTarget && (navType === 'screen' || (!navType && navTarget.startsWith('/')))
            ? navTarget.startsWith('/')
              ? navTarget
              : `/${navTarget}`
            : null;
        if (screenPath) {
          navigate(screenPath);
        } else if (navType === 'url' && navTarget) {
          window.open(navTarget, '_blank', 'noopener,noreferrer');
        } else {
          navigate('/notifications');
        }
        break;
      }

      // List shared - navigate to watchlist if we have ID
      case 'list_shared':
      case 'share_watchlist':
        if (targetId) {
          navigate(`/watchlist/${encodeURIComponent(targetId)}`);
        } else {
          navigate('/notifications');
        }
        break;

      // Movie shared - navigate to movie detail
      case 'share_movie':
        if (targetId) {
          navigate(`/movie/${encodeURIComponent(targetId)}`);
        } else {
          navigate('/notifications');
        }
        break;

      // Profile shared - navigate to user profile
      case 'share_profile':
        if (targetId) {
          navigate(`/profile/${encodeURIComponent(targetId)}`);
        } else {
          navigate('/notifications');
        }
        break;

      // Person shared - navigate to person (cast/crew) page
      case 'share_person':
        if (targetId) {
          navigate(`/person/${encodeURIComponent(targetId)}`);
        } else {
          navigate('/notifications');
        }
        break;

      // Default - show notifications page
      default:
        console.log(`Unknown notification type: ${type}, navigating to notifications page`);
        navigate('/notifications');
    }

    console.log(`Navigated from notification: ${type} -> ${targetId || 'notifications'}`);
  } catch (error) {
    console.error('Error navigating from notification:', error);
    navigate('/notifications');
  }
};

/**
 * Handle notification tap with window.location fallback
 * Used when React Router is not available (e.g., app in background)
 */
export const navigateFromNotificationWithFallback = (data: NotificationData): void => {
  const normalized = normalizeNotificationData(data);
  console.log('🔔 navigateFromNotificationWithFallback called with:', JSON.stringify(normalized));

  const directTap = coerceTapPathFromNormalized(normalized);
  if (directTap) {
    if (Capacitor.isNativePlatform()) {
      console.log(`🔔 Native: tapPath → ${directTap}`);
      navigateFromNativePushRoute(directTap);
    } else {
      const href = new URL(directTap, window.location.origin).href;
      try {
        const cur = `${window.location.pathname}${window.location.search || ''}`;
        if (cur === directTap || `${window.location.pathname}${window.location.search}` === directTap) {
          console.log('🔔 Web: already on tapPath, skip');
          return;
        }
      } catch {
        // non-fatal
      }
      window.location.assign(href);
    }
    return;
  }

  if (!normalized.type || normalized.type === 'unknown') {
    console.warn('🔔 No valid notification data, redirecting to /notifications');
    if (Capacitor.isNativePlatform()) {
      navigateFromNativePushRoute('/notifications');
    } else {
      window.location.href = '/notifications';
    }
    return;
  }

  const { type, targetId, highlightReview } = normalized;
  let path = '/notifications';

  switch (type) {
    case 'movie_added':
    case 'movie_removed':
    case 'note_modified':
    case 'collaboration_activity':
    case 'watchlist_liked':
    case 'like_watchlist':
    case 'collaborator_added':
    case 'list_shared':
    case 'share_watchlist':
      path = targetId ? `/watchlist/${encodeURIComponent(targetId)}` : '/notifications';
      console.log(`🔔 Watchlist notification: type=${type}, targetId=${targetId}, path=${path}`);
      break;

    case 'comment_added_on_watchlist':
      path = targetId ? `/watchlist/${encodeURIComponent(targetId)}?scrollTo=comments` : '/notifications';
      console.log(`🔔 Watchlist comment: targetId=${targetId}, path=${path}`);
      break;

    case 'comment_added_on_review': {
      const hr = highlightReview;
      path = targetId
        ? `/movie/${encodeURIComponent(targetId)}${hr ? `?highlightReview=${encodeURIComponent(hr)}&scrollTo=comments` : ''}`
        : '/notifications';
      console.log(`🔔 Review comment: targetId=${targetId}, highlightReview=${hr}, path=${path}`);
      break;
    }

    case 'bundle_liked':
      path = targetId ? `/?bundleId=${encodeURIComponent(targetId)}&openComments=true` : '/';
      console.log(`🔔 Bundle liked: bundleId=${targetId}, path=${path}`);
      break;

    case 'comment_added_on_bundle':
      path = targetId ? `/?bundleId=${encodeURIComponent(targetId)}&openComments=true` : '/';
      console.log(`🔔 Bundle comment: bundleId=${targetId}, path=${path}`);
      break;

    case 'share_movie':
      path = targetId ? `/movie/${encodeURIComponent(targetId)}` : '/notifications';
      console.log(`🔔 Movie share notification: type=${type}, targetId=${targetId}, path=${path}`);
      break;

    case 'share_profile':
      path = targetId ? `/profile/${encodeURIComponent(targetId)}` : '/notifications';
      console.log(`🔔 Profile share notification: type=${type}, targetId=${targetId}, path=${path}`);
      break;

    case 'share_person':
      path = targetId ? `/person/${encodeURIComponent(targetId)}` : '/notifications';
      console.log(`🔔 Person share notification: type=${type}, targetId=${targetId}, path=${path}`);
      break;

    // Collaboration invitation - navigate to notifications page to accept/decline
    case 'collaboration_invite':
    case 'collaboration_invitation':
      path = '/notifications';
      console.log(`🔔 Collaboration invitation: navigating to notifications page for accept/decline`);
      break;

    case 'new_follower':
    case 'user_followed':
      path = targetId ? `/profile/${encodeURIComponent(targetId)}` : '/notifications';
      console.log(`🔔 Follow notification: type=${type}, targetId=${targetId}, path=${path}`);
      break;

    case 'review_liked': {
      if (targetId) {
        const q = highlightReview ? `?highlightReview=${encodeURIComponent(highlightReview)}` : '';
        path = `/movie/${encodeURIComponent(targetId)}${q}`;
      } else {
        path = '/notifications';
      }
      console.log(`🔔 Review liked: targetId=${targetId}, highlightReview=${highlightReview}, path=${path}`);
      break;
    }
    case 'movie_recommendation':
    case 'movie_watched':
      path = targetId ? `/movie/${encodeURIComponent(targetId)}` : '/notifications';
      console.log(`🔔 Movie notification: type=${type}, targetId=${targetId}, path=${path}`);
      break;

    case 'crew_post_liked': {
      const postId =
        targetId ||
        coercePushString(normalized.relatedCrewPostId as any) ||
        coercePushString(normalized.postId as any);
      path = postId ? `/?postId=${encodeURIComponent(postId)}&openComments=true` : '/';
      console.log(`🔔 Crew post liked: postId=${postId}, path=${path}`);
      break;
    }
    case 'comment_added_on_crew_post': {
      const postId =
        targetId ||
        coercePushString(normalized.relatedCrewPostId as any) ||
        coercePushString(normalized.postId as any);
      path = postId ? `/?postId=${encodeURIComponent(postId)}&openComments=true` : '/';
      console.log(`🔔 Crew post comment: postId=${postId}, path=${path}`);
      break;
    }

    case 'comment_liked': {
      const pt = coercePushString(normalized.parentType as any);
      const cid =
        coercePushString(normalized.commentId as any) ||
        coercePushString(normalized.entityId as any);
      const highlight = cid ? `&highlightCommentId=${encodeURIComponent(cid)}` : '';
      if (pt === 'review') {
        const mid =
          targetId ||
          coercePushString(normalized.relatedMovieId as any) ||
          coercePushString(normalized.movieId as any);
        if (mid) {
          const hr =
            coercePushString(normalized.relatedReviewId as any) ||
            coercePushString(normalized.reviewId as any) ||
            highlightReview;
          path = `/movie/${encodeURIComponent(mid)}?scrollTo=comments${hr ? `&highlightReview=${encodeURIComponent(hr)}` : ''}${highlight}`;
        } else {
          path = '/notifications';
        }
      } else if (pt === 'crewPost') {
        const pid =
          targetId ||
          coercePushString(normalized.relatedCrewPostId as any) ||
          coercePushString(normalized.crewPostId as any) ||
          coercePushString(normalized.postId as any);
        path = pid ? `/?postId=${encodeURIComponent(pid)}&openComments=true${highlight}` : '/';
      } else if (pt === 'watchlist') {
        const wid =
          targetId ||
          coercePushString(normalized.relatedWatchlistId as any) ||
          coercePushString(normalized.watchlistId as any);
        path = wid ? `/watchlist/${encodeURIComponent(wid)}?scrollTo=comments${highlight}` : '/notifications';
      } else if (pt === 'bundle') {
        const bid = targetId || coercePushString(normalized.bundleId as any);
        path = bid ? `/?bundleId=${encodeURIComponent(bid)}&openComments=true${highlight}` : '/';
      } else {
        path = '/notifications';
      }
      console.log(`🔔 Comment liked: parentType=${pt}, path=${path}`);
      break;
    }

    case 'access_approved':
    case 'access_rejected':
      path = '/';
      console.log(`🔔 Access notification: type=${type}, path=${path}`);
      break;

    case 'custom_announcement': {
      const navType = normalized.navigationType;
      const navTarget = normalized.navigationTarget;
      if (navTarget && (navType === 'screen' || (!navType && navTarget.startsWith('/')))) {
        path = navTarget.startsWith('/') ? navTarget : `/${navTarget}`;
      } else if (navType === 'url' && navTarget) {
        window.open(navTarget, '_blank', 'noopener,noreferrer');
        return;
      } else {
        path = '/notifications';
      }
      console.log(`🔔 Custom announcement: path=${path}`);
      break;
    }

    default:
      console.log(`🔔 Unknown notification type: ${type}, defaulting to /notifications`);
  }

  if (Capacitor.isNativePlatform()) {
    console.log(`🔔 Native: SPA navigate to ${path} (not location.href)`);
    navigateFromNativePushRoute(path);
    return;
  }

  // Web / PWA fallback: full navigation
  if (window.location.pathname !== path) {
    console.log(`🔔 Navigating from ${window.location.pathname} to ${path}`);
    window.location.href = path;
  } else {
    console.log(`🔔 Already on target path: ${path}, skipping navigation`);
  }
};
